import cn from "classnames";
import React from "react";
import Button from "web/react/components/button/button";
import GenericStickyModal from "web/react/components/generic-sticky-modal/generic-sticky-modal";
import ProductGalleryCarousel from "web/react/components/product-gallery/product-gallery-carousel/product-gallery-carousel";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import history from "web/script/utils/history";
import { ProductPreviewSerializer } from "web/types/serializers";
import { PRODUCT_PREVIEW_SIZE_GUIDE_OVERLAY_NAME } from "web/react/components/product-preview-size-guide/product-preview-size-guide";
import styles from "./product-preview.css";

export const PRODUCT_PREVIEW_OVERLAY_NAME = "product-preview";

export default function ProductPreviewModal(): JSX.Element | null {
    const overlay = useOverlayWatcher(PRODUCT_PREVIEW_OVERLAY_NAME);
    const isOpen = !!overlay;

    // at some point we could probably use suspense for data fetching here
    const { data, isFetching } = useRothko<ProductPreviewSerializer>(
        "modules/product/product_preview",
        { link_id: overlay && overlay.linkId },
        { enabled: isOpen }
    );

    function onClose(): void {
        analytics.event("pdp_preview", "modal_dismissed");
        history.popOverlay(PRODUCT_PREVIEW_OVERLAY_NAME);
    }

    if (isFetching) {
        return null;
    }

    return (
        <GenericStickyModal className={styles.modal} isOpen={isOpen} onClose={onClose}>
            <ProductPreviewModalInner preview={data!} />
        </GenericStickyModal>
    );
}

interface ProductPreviewModalInnerProps {
    preview: ProductPreviewSerializer;
}

function ProductPreviewModalInner({
    preview,
}: ProductPreviewModalInnerProps): React.ReactElement | null {
    function onSizeGuideClick(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();

        history.pushOverlay(PRODUCT_PREVIEW_SIZE_GUIDE_OVERLAY_NAME, {
            data: { sizeGuideLink: preview.size_guide_link },
        });
    }

    return (
        <div>
            {preview.images && preview.images.length > 0 && (
                <div className={styles.gallery}>
                    <ProductGalleryCarousel
                        analyticsAction={""}
                        settings={{
                            speed: 200,
                            initialSlide: 0,
                            slidesToShow: preview.images.length > 1 ? 1.3 : 1,
                            arrows: false,
                            dots: false,
                            lazyLoad: "progressive",
                            centerMode: false,
                            infinite: true,
                            variableWidth: true,
                            swipe: true,
                            touchMove: true,
                            touchThreshold: 20,
                            draggable: true,
                        }}
                        isFullscreen={false}
                        images={preview.images}
                        hasRelatedProductsSidebar={false}
                    />
                </div>
            )}
            <div className={styles.content}>
                {preview.sizes && preview.sizes.length > 0 && (
                    <>
                        <div className={cn(styles.header, "mb30")}>
                            <h4 className={styles.heading}>
                                {gettext("product.buy_area.sizes_available.label")}
                            </h4>
                            <a className={styles.link} onClick={onSizeGuideClick} href="#">
                                {gettext("product.buy_area.view_size_guide")}
                            </a>
                        </div>
                        <div className="mb15">
                            <SizeAvailability sizes={preview.sizes} />
                        </div>
                    </>
                )}
                <div className={styles.buttons}>
                    <Button
                        fullWidth={true}
                        href={preview.product_link}
                        className={cn("mr10", styles.cta)}
                        styleType="cta"
                    >
                        {gettext("product.buy_area.see_more_details.cta")}
                        <SVGIcon className="ml10" name="arrow-icon" />
                    </Button>
                    <SaveForLaterButton
                        productId={preview.id}
                        pageSource="feed"
                        linkId={preview.link_id}
                        showPdpSignUp
                        type="icon"
                        isLoggedIn={userProfiler.isLoggedIn()}
                    />
                </div>
            </div>
        </div>
    );
}

interface SizeAvailabilityProps {
    sizes: { label: string; in_stock: boolean }[];
}

export function SizeAvailability({ sizes }: SizeAvailabilityProps): JSX.Element | null {
    return (
        <div className={cn(styles.sizes, "pb20")}>
            {sizes.map((size) => (
                <span
                    className={cn(styles.size, {
                        [styles.outOfStock]: !size.in_stock,
                    })}
                    key={size.label}
                    {...(!size.in_stock && {
                        ["aria-label"]: gettext("product.buy_area.unavailable_size.label", {
                            size: size.label,
                        }),
                    })}
                >
                    {size.label}
                </span>
            ))}
        </div>
    );
}
